import { Button, Modal } from '@quickreport/ui';
import clsx from 'clsx';
import { Download, Printer } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktop } from '../../lib/desktop';
import { loadProfile, printSlipHtml } from '../../lib/desktop-print';
import { useWorkspace } from '../../lib/workspace';
import { renderSlipPdf } from '../../pdf/client';
import { PdfPages } from '../../pdf/PdfPages';
import { loadSlipData, printSlipInBrowser, type SlipPaper, slipHtml, slipPdfInput } from './slip';

const PAPERS: { value: SlipPaper; label: string; note: string }[] = [
  { value: 'thermal80', label: 'Thermal receipt', note: '80 mm roll' },
  { value: 'a5', label: 'A5 page', note: 'half an A4 sheet' },
];
const KEY = 'labview.slipPaper';

function savedPaper(): SlipPaper {
  try {
    return localStorage.getItem(KEY) === 'a5' ? 'a5' : 'thermal80';
  } catch {
    return 'thermal80';
  }
}

/**
 * The counter slip: pick the paper, see the slip, then download it or print it. The slip is a
 * real PDF made on this computer, so Download always works, whatever the printer or browser does.
 */
export function SlipDialog({ visitId, onClose }: { visitId: string; onClose: () => void }) {
  const ws = useWorkspace();
  const [paper, setPaper] = useState<SlipPaper>(savedPaper);
  const [pdf, setPdf] = useState<Uint8Array | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const run = useRef(0);

  // Make the slip again whenever the paper changes (or the person asks to try again).
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` only restarts the render
  useEffect(() => {
    const mine = ++run.current;
    setPdf(null);
    setHtml(null);
    setError(null);
    void (async () => {
      try {
        const data = await loadSlipData(
          ws.db,
          ws.orgId,
          ws.auth.orgName,
          visitId,
          ws.auth.verifyBaseUrl,
          paper,
        );
        const input = slipPdfInput(data);
        const r = await Promise.race([
          renderSlipPdf(input.data, input.assets),
          new Promise<never>((_, no) =>
            setTimeout(() => no(new Error('The slip is taking too long to make.')), 45_000),
          ),
        ]);
        if (mine !== run.current) return;
        if (!r.pdf) throw new Error(r.error ?? 'The slip could not be made');
        setPdf(r.pdf);
        setHtml(slipHtml(data));
      } catch (err) {
        if (mine === run.current)
          setError(err instanceof Error ? err.message : 'The slip could not be made');
      }
    })();
  }, [ws.db, ws.orgId, ws.auth.orgName, ws.auth.verifyBaseUrl, visitId, paper, attempt]);

  const url = useMemo(
    () =>
      pdf ? URL.createObjectURL(new Blob([pdf as BlobPart], { type: 'application/pdf' })) : null,
    [pdf],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  const choose = (p: SlipPaper) => {
    setPaper(p);
    try {
      localStorage.setItem(KEY, p);
    } catch {
      // Private mode: the choice is just not remembered.
    }
  };

  const print = useCallback(async () => {
    setError(null);
    try {
      if (isDesktop() && html) {
        const r = await printSlipHtml(html, paper, loadProfile());
        if (!r.ok) throw new Error(r.error ?? 'The slip could not be printed');
      } else if (url && pdf) {
        await printSlipInBrowser(pdf, url, paper);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The slip could not be printed');
    }
  }, [html, paper, url, pdf]);

  const ready = !!pdf && !!url;
  const fileName = `counter-slip-${paper === 'a5' ? 'A5' : '80mm'}.pdf`;

  return (
    <Modal title="Counter slip" label="Counter slip" onClose={onClose}>
      <p className="text-[13px] text-slate">
        A receipt for the patient with every test of this visit. When the visit has reached the
        server it also carries a QR to track progress.
      </p>

      <fieldset className="grid grid-cols-2 gap-2">
        <legend className="sr-only">Paper</legend>
        {PAPERS.map((p) => (
          <label
            key={p.value}
            className={clsx(
              'flex cursor-pointer flex-col rounded-lg border px-3 py-2 text-[13px]',
              paper === p.value ? 'border-brand bg-brand-soft' : 'border-line hover:bg-canvas',
            )}
          >
            <input
              type="radio"
              name="slip-paper"
              value={p.value}
              checked={paper === p.value}
              onChange={() => choose(p.value)}
              className="sr-only"
            />
            <span className="font-medium text-ink">{p.label}</span>
            <span className="text-slate">{p.note}</span>
          </label>
        ))}
      </fieldset>

      <div
        className="flex h-72 flex-col overflow-hidden rounded-lg border border-line bg-canvas"
        aria-busy={!ready && !error}
      >
        {ready && pdf ? (
          <PdfPages pdf={pdf} />
        ) : error ? (
          <div className="grid flex-1 place-items-center gap-2 p-4 text-center">
            <p role="alert" className="text-[13px] text-flag">
              {error}
            </p>
            <Button size="sm" onClick={() => setAttempt((n) => n + 1)}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="grid flex-1 place-items-center text-[13px] text-slate" role="status">
            Preparing the slip…
          </div>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        <a
          href={url ?? undefined}
          download={fileName}
          aria-disabled={!ready}
          className={clsx(
            'inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-navy/25 bg-paper px-3 font-medium text-navy hover:bg-brand-soft',
            !ready && 'pointer-events-none opacity-50',
          )}
        >
          <Download size={15} /> Download PDF
        </a>
        <Button
          tone="primary"
          className="h-10 justify-center"
          disabled={!ready}
          onClick={() => void print()}
        >
          <Printer size={15} /> Print
        </Button>
      </div>
      <p className="text-[12px] text-slate">
        If printing does not start on this device, press Download PDF and print the file from there.
      </p>
      <div className="flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>
    </Modal>
  );
}
